import type React from "react";

import { useState, useEffect, useCallback } from "react";
import {
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  IconButton,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Avatar,
  Chip,
  Tooltip,
  Container,
  useTheme,
  alpha,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { getAllListings, toggleListingActiveStatus } from "../../apis/LaneCageApi";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from "@mui/icons-material/Image";
import SportsBaseballIcon from "@mui/icons-material/SportsBaseball";
import EventIcon from "@mui/icons-material/Event";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewListIcon from "@mui/icons-material/ViewList";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "axios";
import { backendURL } from "../../url";

interface Lane {
  id: string;
  name: string;
  type: string;
  pricePerHour: number;
  image: string;
  size: string;
  description?: string;
  active: boolean;
}

const DEFAULT_LISTING_DESCRIPTION =
  "Experience the best sports facility in town.";

const ManageLanes = () => {
  const theme = useTheme();
  const [lanes, setLanes] = useState<Lane[]>([]);
  const [name, setName] = useState("");
  const [type, setType] = useState("cage");
  const [pricePerHour, setPricePerHour] = useState<number | string>("");
  const [size, setSize] = useState("");
  const [description, setDescription] = useState(DEFAULT_LISTING_DESCRIPTION);
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );
  const [viewMode, setViewMode] = useState(0); // 0 for card view, 1 for table view
  const [loading, setLoading] = useState(false);

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState("");
  const [editName, setEditName] = useState("");
  const [editType, setEditType] = useState("");
  const [editPrice, setEditPrice] = useState<number | string>("");
  const [editSize, setEditSize] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editImage, setEditImage] = useState<File | null>(null);
  const [editImagePreview, setEditImagePreview] = useState<string | null>(null);

  const fetchLanes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllListings(false); // Fetch all listings (including inactive) for admin
      setLanes(data.laneOrCages);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching lanes:", error);
      showSnackbar("Failed to load lanes", "error");
      setLoading(false);
    }
  }, []) 

  useEffect(() => {
    fetchLanes();
  }, [fetchLanes]);

  const showSnackbar = (
    message: string,
    severity: "success" | "error" = "success"
  ) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const capitalizeFirstLetter = (string?: string) => {
    if (!string) return "";
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setImage(selectedFile);

      // Create preview
      const objectUrl = URL.createObjectURL(selectedFile);
      setImagePreview(objectUrl);
    }
  };

  const handleEditImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setEditImage(selectedFile);

      // Create preview
      const objectUrl = URL.createObjectURL(selectedFile);
      setEditImagePreview(objectUrl);
    }
  };

  const handleAddLane = async () => {
    if (!name) {
      showSnackbar("Please enter a lane name", "error");
      return;
    }

    if (!pricePerHour) {
      showSnackbar("Please enter a valid price", "error");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("name", name);
      formData.append("type", type);
      formData.append("pricePerHour", String(pricePerHour));
      formData.append("size", size);
      formData.append("description", description);

      if (image) {
        formData.append("image", image);
      }

      const response = await axios.post(
        `${backendURL}/api/listing/create-listing`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const newLane = response.data;
      setLanes([...lanes, newLane]);

      // Reset form
      setName("");
      setType("cage");
      setPricePerHour("");
      setSize("");
      setDescription(DEFAULT_LISTING_DESCRIPTION);
      setImage(null);
      setImagePreview(null);

      showSnackbar("Lane added successfully!");
      setLoading(false);
    } catch (error) {
      console.error("Error adding lane:", error);
      showSnackbar("Failed to add listing", "error");
      setLoading(false);
    }
  };

  const handleViewChange = (event: React.SyntheticEvent, newValue: number) => {
    setViewMode(newValue);
  };

  const handleDeleteLane = async (id: string) => {
    setLoading(true);
    try {
      await axios.delete(`${backendURL}/api/listing/delete-listing/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          id,
        },
      });
      setLanes(lanes.filter((lane) => lane.id !== id));
      showSnackbar("Lane deleted successfully!");
      setLoading(false);
    } catch (error) {
      console.error("Error deleting lane:", error);
      showSnackbar("Failed to delete listing", "error");
      setLoading(false);
    }
  };

  const handleToggleActiveStatus = async (id: string, currentStatus: boolean) => {
    setLoading(true);
    try {
      const newStatus = !currentStatus;
      await toggleListingActiveStatus(id, newStatus);
      
      // Update the local state
      setLanes(lanes.map((lane) =>
        lane.id === id ? { ...lane, active: newStatus } : lane
      ));
      
      showSnackbar(`Listing ${newStatus ? 'activated' : 'deactivated'} successfully!`);
      setLoading(false);
    } catch (error) {
      console.error("Error toggling active status:", error);
      showSnackbar("Failed to update listing status", "error");
      setLoading(false);
    }
  };

  const handleOpenEditDialog = (id: string) => {
    const laneToEdit = lanes.find((lane) => lane.id === id);
    if (laneToEdit) {
      setEditId(id);
      setEditName(laneToEdit.name);
      setEditType(laneToEdit.type);
      setEditPrice(laneToEdit.pricePerHour);
      setEditSize(laneToEdit.size || "");
      setEditDescription(
        laneToEdit.description || DEFAULT_LISTING_DESCRIPTION
      );
      setEditImagePreview(laneToEdit.image || null);
      setDialogOpen(true);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    resetEditForm();
  };

  const resetEditForm = () => {
    setEditId("");
    setEditName("");
    setEditType("");
    setEditPrice("");
    setEditSize("");
    setEditDescription("");
    setEditImage(null);
    setEditImagePreview(null);
  };

  const handleSaveUpdate = async () => {
    if (!editName) {
      showSnackbar("Please enter a lane name", "error");
      return;
    }

    if (!editPrice) {
      showSnackbar("Please enter a valid price", "error");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("name", editName);
      formData.append("type", editType);
      formData.append("pricePerHour", String(editPrice));
      formData.append("size", editSize);
      formData.append("description", editDescription);

      if (editImage) {
        formData.append("image", editImage);
      }

      const response = await axios.put(
        `${backendURL}/api/listing/update-listing/${editId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const updatedLane = response.data;

      // Update the local state
      setLanes(
        lanes.map((lane) =>
          lane.id === editId
            ? {
                ...lane,
                name: editName,
                type: editType,
                pricePerHour: Number(editPrice),
                size: editSize,
                description: editDescription,
                image: updatedLane.image || lane.image,
              }
            : lane
        )
      );

      handleCloseDialog();
      showSnackbar("Lane updated successfully!");
      setLoading(false);
    } catch (error) {
      console.error("Error updating lane:", error);
      showSnackbar("Failed to update listing", "error");
      setLoading(false);
    }
  };

  const getTypeIcon = (type: string): React.ReactElement | undefined => {
    switch (type) {
      case "lane":
        return <SportsBaseballIcon fontSize="small" />;
      case "event":
        return <EventIcon fontSize="small" />;
      default:
        return undefined;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "cage":
        return theme.palette.primary.main;
      case "lane":
        return theme.palette.secondary.main;
      case "full_facility":
        return theme.palette.success.main;
      case "training_cage":
        return theme.palette.warning.main;
      case "event":
        return theme.palette.info.main;
      default:
        return theme.palette.grey[500];
    }
  };

  return (
    <Container maxWidth="xl">
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          background: `linear-gradient(to right, ${alpha(
            theme.palette.primary.light,
            0.1
          )}, ${alpha(theme.palette.background.paper, 0.7)})`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h5" fontWeight="bold" color="primary">
            Manage Lanes & Cages
          </Typography>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchLanes} color="primary" disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Divider sx={{ mb: 3 }} />

        {/* Add Lane Form */}
        <Card
          elevation={2}
          sx={{
            mb: 4,
            borderRadius: 2,
            overflow: "visible",
            position: "relative",
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -15,
              left: 20,
              bgcolor: theme.palette.primary.main,
              color: "white",
              px: 2,
              py: 0.5,
              borderRadius: 1,
              boxShadow: 1,
            }}
          >
            <Typography variant="subtitle2">Add New</Typography>
          </Box>
          <CardContent sx={{ pt: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Name"
                  variant="outlined"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter lane/cage name"
                  InputProps={{
                    sx: { borderRadius: 1 },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={type}
                    label="Type"
                    onChange={(e) => setType(e.target.value)}
                    sx={{ borderRadius: 1 }}
                  >
                    <MenuItem value="cage">Cage</MenuItem>
                    <MenuItem value="lane">Lane</MenuItem>
                    {/* <MenuItem value="full_facility">
                      Full Facility Rental
                    </MenuItem>
                    <MenuItem value="training_cage">Training Cage</MenuItem> */}
                    <MenuItem value="event">Event</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  label="Price Per Hour ($)"
                  variant="outlined"
                  type="number"
                  value={pricePerHour}
                  onChange={(e) => setPricePerHour(e.target.value)}
                  InputProps={{
                    inputProps: { min: 0 },
                    sx: { borderRadius: 1 },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Size"
                  variant="outlined"
                  type="text"
                  value={size}
                  onChange={(e) => setSize(e.target.value)}
                  placeholder="e.g., 30x40 ft"
                  InputProps={{
                    sx: { borderRadius: 1 },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Description"
                  variant="outlined"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Experience the best sports facility in town."
                  InputProps={{
                    sx: { borderRadius: 1 },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<ImageIcon />}
                  sx={{
                    height: "56px",
                    width: "100%",
                    borderRadius: 1,
                    borderStyle: "dashed",
                    borderWidth: "2px",
                  }}
                >
                  {image ? "Change Image" : "Upload Image"}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </Button>
                {imagePreview && (
                  <Box sx={{ mt: 2, display: "flex", alignItems: "center" }}>
                    <Avatar
                      src={imagePreview}
                      variant="rounded"
                      sx={{ width: 56, height: 56, mr: 2, boxShadow: 1 }}
                    />
                    <Typography variant="body2">
                      Image selected
                      <IconButton
                        size="small"
                        onClick={() => {
                          setImage(null);
                          setImagePreview(null);
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Typography>
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleAddLane}
                  sx={{
                    height: "56px",
                    borderRadius: 1,
                    boxShadow: 2,
                  }}
                  disabled={loading}
                >
                  {loading ? "Adding..." : "Add Lane/Cage"}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* View Toggle */}
        <Box
          sx={{
            mb: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" color="text.secondary">
            {lanes.length} {lanes.length === 1 ? "Item" : "Items"} Available
          </Typography>
          <Tabs
            value={viewMode}
            onChange={handleViewChange}
            sx={{
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: 1.5,
              },
            }}
          >
            <Tab
              icon={<ViewModuleIcon />}
              label="Card View"
              sx={{
                borderRadius: "8px 0 0 8px",
                minHeight: "auto",
                py: 1,
              }}
            />
            <Tab
              icon={<ViewListIcon />}
              label="Table View"
              sx={{
                borderRadius: "0 8px 8px 0",
                minHeight: "auto",
                py: 1,
              }}
            />
          </Tabs>
        </Box>

        {/* Card View */}
        {viewMode === 0 && (
          <Grid container spacing={3}>
            {lanes.length > 0 ? (
              lanes.map((lane) => (
                <Grid item xs={12} sm={6} md={4} key={lane.id}>
                  <Card
                    elevation={2}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: 2,
                      overflow: "hidden",
                      transition: "transform 0.2s, box-shadow 0.2s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 6,
                      },
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "5px",
                        bgcolor: getTypeColor(lane.type),
                      }}
                    />
                    {lane.image ? (
                      <Box sx={{ p: 0, height: 160, position: "relative" }}>
                        <Box
                          component="img"
                          src={lane.image}
                          alt={lane.name}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 0,
                            right: 0,
                            bgcolor: "rgba(0,0,0,0.6)",
                            color: "white",
                            px: 1,
                            py: 0.5,
                            borderTopLeftRadius: 8,
                          }}
                        >
                          {lane.size && (
                            <Typography variant="caption" fontWeight="bold">
                              {lane.size}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          height: 120,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: alpha(getTypeColor(lane.type), 0.1),
                        }}
                      >
                        <ImageIcon
                          sx={{ fontSize: 60, color: "text.disabled" }}
                        />
                      </Box>
                    )}
                    <CardContent sx={{ flexGrow: 1, pt: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="h6"
                          component="div"
                          fontWeight="bold"
                        >
                          {lane.name}
                        </Typography>
                        <Chip
                          label={capitalizeFirstLetter(lane.type)}
                          size="small"
                          icon={getTypeIcon(lane.type)}
                          sx={{
                            bgcolor: alpha(getTypeColor(lane.type), 0.1),
                            color: getTypeColor(lane.type),
                            fontWeight: "bold",
                          }}
                        />
                      </Box>
                      <Typography
                        variant="h6"
                        color="primary"
                        sx={{
                          mt: 2,
                          display: "inline-block",
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                        }}
                      >
                        ${lane.pricePerHour}/hour
                      </Typography>
                      {lane.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1.5 }}
                        >
                          {lane.description}
                        </Typography>
                      )}
                      
                      {/* Active Status Toggle */}
                      <Box sx={{ mt: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography variant="body2" color="text.secondary">
                          Status:
                        </Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={lane.active}
                              onChange={() => handleToggleActiveStatus(lane.id, lane.active)}
                              color="primary"
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="body2" color={lane.active ? "success.main" : "error.main"}>
                              {lane.active ? "Active" : "Inactive"}
                            </Typography>
                          }
                          sx={{ m: 0 }}
                        />
                      </Box>
                    </CardContent>
                    <Divider />
                    <Box sx={{ display: "flex", p: 1 }}>
                      <Tooltip title="Edit">
                        <Button
                          size="small"
                          color="primary"
                          onClick={() => handleOpenEditDialog(lane.id)}
                          startIcon={<EditIcon />}
                          sx={{ flex: 1, mr: 1 }}
                        >
                          Edit
                        </Button>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <Button
                          size="small"
                          color="error"
                          onClick={() => handleDeleteLane(lane.id)}
                          startIcon={<DeleteIcon />}
                          sx={{ flex: 1 }}
                        >
                          Delete
                        </Button>
                      </Tooltip>
                    </Box>
                  </Card>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Paper
                  sx={{
                    p: 4,
                    textAlign: "center",
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.light, 0.05),
                    border: `1px dashed ${theme.palette.divider}`,
                  }}
                >
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No lanes or cages available
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Add your first lane or cage using the form above
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        )}

        {/* Table View */}
        {viewMode === 1 && (
          <TableContainer
            component={Paper}
            elevation={2}
            sx={{
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Table>
              <TableHead>
                <TableRow
                  sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                >
                  <TableCell sx={{ fontWeight: "bold" }}>Image</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Size</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Price Per Hour
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lanes.length > 0 ? (
                  lanes.map((lane) => (
                    <TableRow
                      key={lane.id}
                      hover
                      sx={{
                        "&:hover": {
                          bgcolor: alpha(getTypeColor(lane.type), 0.05),
                        },
                      }}
                    >
                      <TableCell>
                        {lane.image ? (
                          <Avatar
                            src={lane.image}
                            variant="rounded"
                            sx={{ width: 50, height: 50, boxShadow: 1 }}
                          />
                        ) : (
                          <Avatar
                            variant="rounded"
                            sx={{
                              width: 50,
                              height: 50,
                              bgcolor: alpha(getTypeColor(lane.type), 0.1),
                            }}
                          >
                            <ImageIcon color="action" />
                          </Avatar>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" fontWeight="medium">
                          {lane.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={capitalizeFirstLetter(lane.type)}
                          size="small"
                          icon={getTypeIcon(lane.type)}
                          sx={{
                            bgcolor: alpha(getTypeColor(lane.type), 0.1),
                            color: getTypeColor(lane.type),
                            fontWeight: "medium",
                          }}
                        />
                      </TableCell>
                      <TableCell>{lane.size || "-"}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          sx={{
                            color: theme.palette.primary.main,
                            display: "inline-block",
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                          }}
                        >
                          ${lane.pricePerHour}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={lane.active}
                              onChange={() => handleToggleActiveStatus(lane.id, lane.active)}
                              color="primary"
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="body2" color={lane.active ? "success.main" : "error.main"}>
                              {lane.active ? "Active" : "Inactive"}
                            </Typography>
                          }
                          sx={{ m: 0 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenEditDialog(lane.id)}
                            sx={{ mr: 1 }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteLane(lane.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No lanes or cages available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Edit Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: 2,
              overflow: "hidden",
            },
          }}
        >
          <DialogTitle
            sx={{
              m: 0,
              p: 2,
              bgcolor: theme.palette.primary.main,
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6">Edit Lane/Cage</Typography>
            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
              sx={{
                color: "white",
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            <Stack spacing={3} sx={{ pt: 1 }}>
              <TextField
                label="Name"
                variant="outlined"
                fullWidth
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                InputProps={{
                  sx: { borderRadius: 1 },
                }}
              />
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={editType}
                  label="Type"
                  onChange={(e) => setEditType(e.target.value)}
                  sx={{ borderRadius: 1 }}
                >
                  <MenuItem value="cage">Cage</MenuItem>
                  <MenuItem value="lane">Lane</MenuItem>
                  <MenuItem value="full_facility">
                    Full Facility Rental
                  </MenuItem>
                  <MenuItem value="training_cage">Training Cage</MenuItem>
                  <MenuItem value="event">Event</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Price Per Hour ($)"
                variant="outlined"
                fullWidth
                type="number"
                value={editPrice}
                onChange={(e) => setEditPrice(e.target.value)}
                InputProps={{
                  inputProps: { min: 0 },
                  sx: { borderRadius: 1 },
                }}
              />
              <TextField
                label="Size"
                variant="outlined"
                fullWidth
                type="text"
                value={editSize}
                onChange={(e) => setEditSize(e.target.value)}
                placeholder="e.g., 30x40 ft"
                InputProps={{
                  sx: { borderRadius: 1 },
                }}
              />
              <TextField
                label="Description"
                variant="outlined"
                fullWidth
                multiline
                minRows={3}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="Experience the best sports facility in town."
                InputProps={{
                  sx: { borderRadius: 1 },
                }}
              />

              {/* Image Upload Section */}
              <Box>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<ImageIcon />}
                  fullWidth
                  sx={{
                    borderRadius: 1,
                    borderStyle: "dashed",
                    borderWidth: "2px",
                    p: 1.5,
                  }}
                >
                  {editImage ? "Change Image" : "Upload Image"}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleEditImageChange}
                  />
                </Button>

                {(editImagePreview || editImage) && (
                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        height: 200,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "grey.100",
                        borderRadius: 1,
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      {editImagePreview && (
                        <>
                          <Box
                            component="img"
                            src={editImagePreview}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                            }}
                            alt="Preview"
                          />
                          <IconButton
                            size="small"
                            sx={{
                              position: "absolute",
                              top: 8,
                              right: 8,
                              bgcolor: "rgba(0,0,0,0.5)",
                              color: "white",
                              "&:hover": {
                                bgcolor: "rgba(0,0,0,0.7)",
                              },
                            }}
                            onClick={() => {
                              setEditImage(null);
                              setEditImagePreview(null);
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                      {!editImagePreview && (
                        <Typography variant="body2" color="text.secondary">
                          No image selected
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, bgcolor: "background.default" }}>
            <Button
              variant="outlined"
              onClick={handleCloseDialog}
              sx={{ borderRadius: 1 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveUpdate}
              disabled={loading}
              sx={{
                borderRadius: 1,
                boxShadow: 2,
              }}
            >
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Notification */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Paper>
    </Container>
  );
};

export default ManageLanes;
